import { useEffect, useState, useRef } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, onSnapshot } from "firebase/firestore";

export type UserRole = "END_USER" | "STAFF" | "ADMIN" | "KIOSK_ADMIN" | "CLUB" | "SUPER_ADMIN" | null;

interface UserInfo {
  uid: string;
  email: string | null;
  displayName: string | null;
  role: UserRole;
  tenant_id: string | null;
  memberID: string | null;
  user_wallet_id: string | null;
  onboardingCompleted: boolean;
}

interface CacheEntry {
  data: UserInfo | null;
  timestamp: number;
}

/**
 * Hook: Read user role from Firestore with caching
 * 
 * NEW ARCHITECTURE (Single Source of Truth):
 * - Role is read from /users/{uid} collection (not Auth claims)
 * - Cached locally with 5-minute revalidation
 * - Real-time updates when Firestore document changes
 * 
 * Returns:
 * - user: Complete user data including role, tenant, onboarding status
 * - role: Just the role (for convenience)
 * - tenant_id: Just the tenant (for convenience)
 * - onboardingCompleted: Onboarding status (for modals)
 * - loading: Initial data fetch in progress
 * - error: Any errors during fetch
 * - hasRole: Helper to check if user has required role(s)
 */
export function useUserRole() {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const cacheRef = useRef<CacheEntry>({ data: null, timestamp: 0 });
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  useEffect(() => {
    const auth = getAuth();
    const db = getFirestore();
    
    const unsubscribe = onAuthStateChanged(
      auth,
      async (authUser) => {
        try {
          if (authUser) {
            // Check cache first
            const now = Date.now();
            const cacheValid = cacheRef.current.timestamp && 
                              (now - cacheRef.current.timestamp < CACHE_DURATION);
            
            if (cacheValid && cacheRef.current.data) {
              setUser(cacheRef.current.data);
              setLoading(false);
              // Continue to set up real-time listener below
            }

            // Set up real-time listener from Firestore (SINGLE SOURCE OF TRUTH)
            const userDocRef = doc(db, "users", authUser.uid);
            const unsubscribeSnapshot = onSnapshot(
              userDocRef,
              (docSnapshot) => {
                if (docSnapshot.exists()) {
                  const firestoreData = docSnapshot.data();
                  
                  const userData: UserInfo = {
                    uid: authUser.uid,
                    email: authUser.email,
                    displayName: authUser.displayName || firestoreData.displayName || null,
                    role: (firestoreData.role || "END_USER") as UserRole,
                    tenant_id: firestoreData.tenant_id || null,
                    memberID: firestoreData.memberID || null,
                    user_wallet_id: firestoreData.user_wallet_id || null,
                    onboardingCompleted: firestoreData.onboardingCompleted || false,
                  };

                  setUser(userData);
                  
                  // Update cache
                  cacheRef.current = {
                    data: userData,
                    timestamp: Date.now(),
                  };

                  setLoading(false);
                } else {
                  setError("User document not found");
                  setLoading(false);
                }
              },
              (err) => {
                console.error("Error reading user role from Firestore:", err);
                setError(err.message);
                setLoading(false);
              }
            );

            return () => unsubscribeSnapshot();
          } else {
            setUser(null);
            setLoading(false);
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : "Failed to fetch user");
          setLoading(false);
        }
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const hasRole = (requiredRoles: UserRole[]): boolean => {
    if (!user) return false;
    return requiredRoles.includes(user.role);
  };

  const isAdmin = (): boolean => {
    return hasRole(["ADMIN", "SUPER_ADMIN"]);
  };

  const isClub = (): boolean => {
    return hasRole(["CLUB", "SUPER_ADMIN"]);
  };

  const isSuperAdmin = (): boolean => {
    return user?.role === "SUPER_ADMIN";
  };

  return {
    user,
    loading,
    error,
    hasRole,
    isAdmin,
    isClub,
    isSuperAdmin,
    role: user?.role || null,
    tenant_id: user?.tenant_id || null,
    onboardingCompleted: user?.onboardingCompleted || false,
    isAuthenticated: user !== null,
    isEndUser: user?.role === "END_USER",
  };
}
